import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, TextField, Typography } from '@mui/material';
import { Fingerprint } from '@mui/icons-material';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useBiometric } from '../hooks/useBiometric';
import { statesUrl } from '../boardApi';
import { MAX_WIDTH } from '../App';

export const LoginScreen: React.FC = () => {
  const { t } = useLanguage();
  const { username, gatewayUrl, login, goToHousePicker, houses, activeHouseId } = useAuth();
  const biometric = useBiometric(activeHouseId ?? '');

  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [biometricError, setBiometricError] = useState(false);
  const [biometricPwdStale, setBiometricPwdStale] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  // Enrollment prompt state (shown after password submit)
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);
  const [showDontAskConfirm, setShowDontAskConfirm] = useState(false);

  // Auto-trigger biometric if enrolled
  useEffect(() => {
    if (biometric.isAvailable && biometric.isEnrolled) {
      handleBiometricLogin();
    }
  }, [biometric.isAvailable, biometric.isEnrolled]); // eslint-disable-line react-hooks/exhaustive-deps

  const validatePassword = async (pwd: string): Promise<boolean> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(statesUrl(`${gatewayUrl}/board/1`), {
        headers: { Authorization: 'Basic ' + btoa(`${username}:${pwd}`) },
        signal: controller.signal,
      });
      clearTimeout(timer);
      return res.status !== 401;
    } catch {
      clearTimeout(timer);
      return true; // network error → let through (offline mode)
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    setBiometricError(false);
    setBiometricPwdStale(false);
    const pwd = await biometric.authenticate();
    if (pwd !== null) {
      const valid = await validatePassword(pwd);
      setBiometricLoading(false);
      if (valid) {
        login(pwd);
      } else {
        setBiometricPwdStale(true);
      }
    } else {
      setBiometricLoading(false);
      setBiometricError(true);
    }
  };

  const handleSubmit = async () => {
    if (!password.trim()) { setError(true); return; }
    setError(false);
    setValidating(true);
    const valid = await validatePassword(password);
    setValidating(false);

    if (!valid) {
      setError(true);
      return;
    }

    // If biometric available, not enrolled, and not dismissed, show enrollment prompt first
    if (biometric.isAvailable && !biometric.isEnrolled && !biometric.isPromptDismissed) {
      setPendingPassword(password);
      return;
    }

    // Already enrolled — update stored password in case it changed
    if (biometric.isEnrolled) biometric.updateStoredPassword(password);
    login(password);
  };

  const handleEnableBiometric = async () => {
    if (!pendingPassword) return;
    await biometric.enroll(pendingPassword);
    login(pendingPassword);
  };

  const handleSkipBiometric = () => {
    if (!pendingPassword) return;
    login(pendingPassword);
  };

  // ── Enrollment prompt ────────────────────────────────────────────────────
  if (pendingPassword !== null) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100dvh',
        pt: 'env(safe-area-inset-top)', px: 3,
      }}>
        <Box sx={{ width: '100%', maxWidth: MAX_WIDTH, textAlign: 'center' }}>
          <Fingerprint sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" mb={1}>{t.biometricEnable}</Typography>

          {showDontAskConfirm ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={4}>
                {t.biometricDontAskConfirm}
              </Typography>
              <Button fullWidth variant="contained" onClick={handleSkipBiometric}
                sx={{ py: 1.5, fontSize: 16, fontWeight: 'bold', borderRadius: 2 }}
              >
                OK
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={4}>{t.biometricEnableDesc}</Typography>
              <Button fullWidth variant="contained" onClick={handleEnableBiometric}
                sx={{ py: 1.5, fontSize: 16, fontWeight: 'bold', borderRadius: 2, mb: 2 }}
              >
                {t.biometricEnable}
              </Button>
              <Button fullWidth variant="text" onClick={handleSkipBiometric} sx={{ mb: 1 }}>
                {t.biometricNotNow}
              </Button>
              <Button fullWidth variant="text" color="inherit"
                onClick={() => { biometric.dismissPrompt(); setShowDontAskConfirm(true); }}
                sx={{ color: 'text.disabled', fontSize: 12 }}
              >
                {t.biometricDontAsk}
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // ── Normal login ─────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100dvh',
      pt: 'env(safe-area-inset-top)', px: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: MAX_WIDTH }}>
        <Typography variant="h4" fontWeight="bold" mb={1}>{t.loginTitle}</Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          {t.loginWelcome(username)}
        </Typography>

        {/* Biometric button */}
        {biometric.isAvailable && biometric.isEnrolled && (
          <Box textAlign="center" mb={3}>
            {biometricLoading ? (
              <CircularProgress color="primary" />
            ) : (
              <ButtonBase
                onClick={handleBiometricLogin}
                aria-label={t.biometricLoginButton}
                sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: 1, p: 1, borderRadius: 2 }}
              >
                <Fingerprint sx={{ fontSize: 72, color: (biometricError || biometricPwdStale) ? 'error.main' : 'primary.main' }} />
                <Typography variant="caption" color={(biometricError || biometricPwdStale) ? 'error' : 'text.secondary'}>
                  {biometricPwdStale ? t.biometricPasswordStale : biometricError ? t.biometricError : t.biometricLoginButton}
                </Typography>
              </ButtonBase>
            )}
          </Box>
        )}

        <TextField
          fullWidth label={t.loginPassword} value={password} type="password"
          onChange={e => { setPassword(e.target.value); setError(false); }}
          onKeyDown={e => e.key === 'Enter' && !validating && handleSubmit()}
          error={error}
          helperText={error ? t.loginWrongPassword : ''}
          sx={{ mb: 3 }}
          autoFocus={!biometric.isEnrolled}
          disabled={validating}
        />

        <Button fullWidth variant={biometric.isEnrolled ? 'outlined' : 'contained'} onClick={handleSubmit}
          disabled={validating}
          sx={{ py: 1.5, fontSize: 16, fontWeight: 'bold', borderRadius: 2 }}
        >
          {validating ? <CircularProgress size={24} color="inherit" /> : t.loginSubmit}
        </Button>

        {houses.length > 1 && (
          <Button fullWidth variant="text" onClick={goToHousePicker} sx={{ mt: 1 }}>
            {t.loginSwitchHouse}
          </Button>
        )}
      </Box>
    </Box>
  );
};
